/**
 * The window every moved `simplified_list` caller asks `adminMerchants` for: active merchants, name
 * order, one page. Django's route defaulted to `active_only=True` for a super user and ordered by
 * name, so this is what it returned. Named here rather than repeated at each call site because the
 * four callers have to agree — a switcher showing a different set from the merge tools is the bug this
 * prevents.
 */
export const ADMIN_MERCHANT_LIST_PARAMS = {
  isActive__exact: 'true',
  sort: 'name',
  limit: 1000,
  page: 1
}

export default {
  // API-gateway routes, reached with `cfAxios` rather than the Django client, and
  // keyed by merchant **pkid** rather than UUID. The generator emits one-line
  // arrows from Django's spec and cannot reproduce these.
  uiPreference: merchantId => `/core/v1/merchant/${merchantId}/ui-preference`,
  // Replaces Django's `retrieveMerchantOrganization` for the store whenever a Firebase token is
  // present. camelCase; read it through `toDjangoMerchantShape` (utils/merchant-record.js).
  merchantRecord: merchantId => `/core/v1/merchant/${merchantId}/record`,
  // Sandbox only: core deploys this route to sandbox alone and refuses every merchant but the
  // Bueller demo merchant.
  demoReset: merchantId => `/core/v1/merchant/${merchantId}/demo-reset`,
  locationSettings: (merchantId, locationId) =>
    `/core/v1/merchant/${merchantId}/location/${locationId}/settings`,
  aiTextsReport: merchantId =>
    `/core/v1/merchant/${merchantId}/report/ai-texts`,
  merchantDmsIntegration: merchantId =>
    `/core/v1/admin/merchant/${merchantId}/dms-integration`,
  // Admin scope: super tenants only, so a merchant-user token gets a 403 from the gateway. Takes no
  // merchant id — unlike its neighbours, it is the list. Rows are camelCase: `{ id, pkid, name,
  // supportEmail, isActive, integrationType, lastJob }`; `lastJob` is null for no job run.
  adminMerchants: () => `/core/v1/admin/merchants`,
  merchantDmsConfig: merchantId =>
    `/connector/v1/admin/merchant/${merchantId}/merchant-config`,
  // GET lists; POST creates and answers the row plus `twilioSetup` ("ok" or "failed").
  merchantLocations: merchantId => `/core/v1/merchant/${merchantId}/locations`,
  // BlytzCollect configuration reads on ai-collector-service, keyed by merchant pkid. camelCase;
  // read them through `toDjangoBlytzCollectShape` (utils/ai-collector-config.js).
  aiAgents: merchantId => `/ai-collector/v1/merchant/${merchantId}/agents`,
  aiBatchConfigs: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/batch-configs`,
  aiBatchConfig: (merchantId, configPkid) =>
    `/ai-collector/v1/merchant/${merchantId}/batch-configs/${configPkid}`,
  aiBatchConfigPreview: (merchantId, configPkid) =>
    `/ai-collector/v1/merchant/${merchantId}/batch-configs/${configPkid}/preview`,
  aiRunBatchNow: (merchantId, configPkid) =>
    `/ai-collector/v1/merchant/${merchantId}/batch-configs/${configPkid}/run-batch-now`,
  aiBatches: merchantId => `/ai-collector/v1/merchant/${merchantId}/batches`,
  aiBatchProgress: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/batches/progress`,
  aiLocationConfigs: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/location-configs`,
  // Admin scope: super tenants only, as Django's IsBlytz was.
  aiAdminLocationConfigs: merchantId =>
    `/ai-collector/v1/admin/merchant/${merchantId}/location-configs`,
  aiAdminLocationConfig: (merchantId, configPkid) =>
    `/ai-collector/v1/admin/merchant/${merchantId}/location-configs/${configPkid}`,
  aiDefaultTransferNumber: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/location-configs/default-transfer-number`,
  // Keyed by location pkid; a UUID here is a 404. camelCase, the list's row shape. GET, PUT with
  // PATCH meaning (any key outside the location fields is a 400), and DELETE (409 while in use).
  merchantLocation: (merchantId, locationPkid) =>
    `/core/v1/merchant/${merchantId}/locations/${locationPkid}`,
  // `{ DEPOSIT, ACHR }` recipient strings. Any other key, IQR and DDR included, is a 400.
  locationEmailReports: (merchantId, locationPkid) =>
    `/core/v1/merchant/${merchantId}/locations/${locationPkid}/email-reports`,
  // Keyed by merchant pkid. `{ tags: [{ id, name, slug, useCount }] }`, every tag, unpaginated.
  accountTags: merchantId => `/core/v1/merchant/${merchantId}/account-tags`,
  // tagId is the tag's integer `id`; the table has no pkid or UUID. Answers 200 `{ id }`, not 204.
  accountTag: (merchantId, tagId) =>
    `/core/v1/merchant/${merchantId}/account-tags/${tagId}`,
  // POST `{ targetId }` moves the tag's accounts onto the target and deletes it; answers the target.
  accountTagMerge: (merchantId, tagId) =>
    `/core/v1/merchant/${merchantId}/account-tags/${tagId}/merge`,
  // POST `{ tagId, fileName, rows }`: classifies each row and writes nothing. Shapes: utils/bulkAccountTags.ts.
  accountTagBulkPreview: merchantId =>
    `/core/v1/merchant/${merchantId}/account-tags/bulk/preview`,
  // POST the preview body plus `previewFingerprint` and `requestId`; 409 when the review went stale.
  accountTagBulk: merchantId =>
    `/core/v1/merchant/${merchantId}/account-tags/bulk`,
  // POST, no body. Removes only the assignments that batch inserted; repeating it is a no-op.
  accountTagBulkUndo: (merchantId, batchPkid) =>
    `/core/v1/merchant/${merchantId}/account-tags/bulk/${batchPkid}/undo`,
  // Merchant and account pkids, not UUIDs. `{ promises }`, camelCase rows.
  accountPromises: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/promises`,
  // Merchant and account pkids. `{ notes, hasMore }`, newest first; POST `{ body }` answers one note.
  accountNotes: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/notes`,
  accountNotesExport: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/notes/export`,
  // Merchant and account pkids. POST, no body; answers `{ lastWorked: { workedAt, workedByName } }`.
  accountMarkWorked: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/mark-worked`,
  // Merchant and account pkids. GET answers `{ followUp }` (null when none is SCHEDULED); POST creates,
  // PUT edits, and `/complete` and `/cancel` close the SCHEDULED one. camelCase.
  accountFollowUp: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/follow-up`,
  accountFollowUpComplete: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/follow-up/complete`,
  accountFollowUpCancel: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/follow-up/cancel`,
  // `{ count, results, counts }`; the export takes the same filters plus `file_format`.
  followUpReport: merchantId => `/core/v1/merchant/${merchantId}/follow-ups`,
  followUpReportExport: merchantId =>
    `/core/v1/merchant/${merchantId}/follow-ups/export`,
  // Merchant and account pkids. `{ count, results }`: Django's rows, with `pkid` beside each UUID `id`.
  // Pages by `page` and camelCase `pageSize`; the export takes `file_format` and `tz`.
  accountPaymentHistory: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/payment-history`,
  accountPaymentHistoryExport: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/payment-history/export`,
  importAccounts: merchantId =>
    `/core/v1/merchant/${merchantId}/import-accounts`,
  optOutReport: merchantId => `/core/v1/merchant/${merchantId}/opt-outs`,
  // The Customers list (one page of customer/account rows) and, with `file_format`, its xlsx export.
  // POST body: {filter, customers, search, ordering, page, page_size}. Keyed by the merchant pkid.
  customerAccounts: merchantId =>
    `/core/v1/merchant/${merchantId}/customer-accounts`,
  // Merchant pkid but customer UUID: the page URL carries the UUID. GET, and PUT with PATCH
  // semantics (the gateway has no PATCH). Django's `voidCustomer` shares Django's path and stays there.
  merchantCustomer: (merchantId, customerUuid) =>
    `/core/v1/merchant/${merchantId}/customers/${customerUuid}`,
  // Merchant and account pkids. `{ customers }`, camelCase rows; a UUID in the path is a 404.
  accountCustomers: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/customers`,
  // Merchant, account and authorized-user pkids. List answers `{ results }`, newest first.
  accountAuthorizedUsers: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/authorized-users`,
  accountAuthorizedUser: (merchantId, accountPkid, authorizedUserPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/authorized-users/${authorizedUserPkid}`,
  // Merchant and account pkids. POST sets the amount, holds it and sends the request, in one call.
  paymentRequest: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/payment-request`,
  // Merchant and account pkids. DELETE cancels a Send Request hold and answers `{ synced }`.
  invoiceLock: (merchantId, accountPkid) =>
    `/core/v1/merchant/${merchantId}/accounts/${accountPkid}/invoice-lock`,
  // Customer groups by pkid; a UUID in the path is a 404. Filter-only: a `customers` body is a 400.
  customerGroups: merchantId =>
    `/core/v1/merchant/${merchantId}/customer-groups`,
  customerGroup: (merchantId, groupPkid) =>
    `/core/v1/merchant/${merchantId}/customer-groups/${groupPkid}`,
  // PUT {visible_group}: whether the Customers filter lists the group under "Start from a saved group".
  customerGroupVisibility: (merchantId, groupPkid) =>
    `/core/v1/merchant/${merchantId}/customer-groups/${groupPkid}/visibility`,
  // POST {filter, channels?, page?, page_size?}: the accounts a group's automation would message.
  customerGroupsPreview: merchantId =>
    `/core/v1/merchant/${merchantId}/customer-groups/preview`,
  // a customer group's automation (template, weekdays, send time, active).
  // the merchant pkid, the gateway's tenant root; groupPkid is the customer-group pkid. A UUID in
  // this path is a 404.
  groupAutomations: merchantId =>
    `/core/v1/merchant/${merchantId}/group-automations`,
  groupAutomation: (merchantId, groupPkid) =>
    `/core/v1/merchant/${merchantId}/customer-groups/${groupPkid}/automation`,
  // The merchant's templates with bodies and `chat_visible`; visibility is set by template pkid.
  merchantMessageTemplates: merchantId =>
    `/core/v1/merchant/${merchantId}/message-templates`,
  templateVisibility: (merchantId, templatePkid) =>
    `/core/v1/merchant/${merchantId}/message-templates/${templatePkid}/visibility`,
  movePayment: merchantId =>
    `/core/v1/admin/merchant/${merchantId}/move-payment`,
  // Not `mergeCustomers`: a regenerated Django builder of that name would overwrite it.
  adminMergeCustomers: merchantId =>
    `/core/v1/admin/merchant/${merchantId}/merge-customers`,
  // Not `mergeAccounts`: that Django name is retired (endpoints.retired.json).
  adminMergeAccounts: merchantId =>
    `/core/v1/admin/merchant/${merchantId}/merge-accounts`,
  accountsContactedSummary: merchantId =>
    `/action/v1/merchant/${merchantId}/account-summary`,
  // BlytzCollect Analytics, from ai-collector-service, keyed by merchant pkid. Both take
  // `dateFrom`/`dateTo` as merchant-local YYYY-MM-DD, inclusive.
  blytzcollectAnalytics: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/report/blytzcollect-analytics`,
  blytzcollectAnalyticsTopAccounts: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/report/blytzcollect-analytics/top-accounts`,
  // a Send the composer refused is reported here; messaging-service posts the Slack alert.
  blockedMessageAttempts: merchantId =>
    `/messaging/v1/merchant/${merchantId}/blocked-message-attempts`,
  // Marks the customer viewed and zeroes their unread count. Keyed by customer pkid; a UUID is a 404.
  customerViewed: (merchantId, customerPkid) =>
    `/messaging/v1/merchant/${merchantId}/customers/${customerPkid}/viewed`,
  // Merchant, account and customer pkids; POST, no body (any key is a 400). A refusal answers 409
  // and a missing account, customer or template 404, each with a `message` written for the merchant.
  accountCustomerInvite: (merchantId, accountPkid, customerPkid) =>
    `/messaging/v1/merchant/${merchantId}/accounts/${accountPkid}/customers/${customerPkid}/invite`,
  // Not `renderMessageTemplate`: endpoints.js spreads this object first, so the generated Django
  // builder of that name would overwrite it.
  messageTemplateRender: (merchantId, templatePkid) =>
    `/messaging/v1/merchant/${merchantId}/message-templates/${templatePkid}/render`,
  // Singular `Summary`, unlike the generated Django builders in endpoints.js, which are plural
  // `Summaries`. The two families serve the same rows; only these three go through the gateway.
  dashboardAutopaySummary: merchantId =>
    `/core/v1/merchant/${merchantId}/dashboard/autopay-summary`,
  dashboardPromiseSummary: merchantId =>
    `/core/v1/merchant/${merchantId}/dashboard/promise-summary`,
  dashboardScheduledSummary: merchantId =>
    `/core/v1/merchant/${merchantId}/dashboard/scheduled-summary`,
  dashboardPaymentOrigins: merchantId =>
    `/core/v1/merchant/${merchantId}/dashboard/payment-origins`,
  // Named for the resource, not the operation: endpoints.js spreads this object first, so a key
  // matching the generated Django `listBusinessOwners` / `createBusinessOwner` /
  // `retrieveBusinessOwner` / `updateBusinessOwner` would be silently overwritten by it.
  businessOwners: merchantId =>
    `/core/v1/merchant/${merchantId}/business-owners`,
  businessOwner: (merchantId, ownerId) =>
    `/core/v1/merchant/${merchantId}/business-owners/${ownerId}`,
  // Merchant pkid and customer UUID. Any query parameter is a 400. `{ paymentMethods }`, camelCase;
  // read it through `fetchCustomerPaymentMethods` (utils/customer-payment-methods.ts).
  customerPaymentMethods: (merchantId, customerId) =>
    `/payment/v1/merchant/${merchantId}/customers/${customerId}/payment-methods`,
  // Bell feed and read marks. Keyed by merchant pkid. Django's spec does not describe them.
  bellEvents: merchantId => `/core/v1/merchant/${merchantId}/events`,
  notificationMarks: merchantId =>
    `/core/v1/merchant/${merchantId}/notification-marks`,
  // Reads only: Django's create, PATCH and submit-to-processor keep the UUID path. Any query
  // parameter but page, pageSize, search, sortBy and orderBy is a 400.
  merchantAccounts: merchantId =>
    `/payment/v1/merchant/${merchantId}/merchant-accounts`,
  // Super users only. Keyed by account pkid; a UUID is a 404. Bank and EIN numbers are `*LastFour`.
  merchantAccount: (merchantId, merchantAccountPkid) =>
    `/payment/v1/merchant/${merchantId}/merchant-accounts/${merchantAccountPkid}`,
  // Not `listMappings` / `createMappings` / `partialUpdateMappings`: endpoints.js spreads this object
  // first, so the generated Django builders of those names would overwrite these. Keyed by pkids.
  csvMappings: merchantId => `/core/v1/merchant/${merchantId}/csv-mappings`,
  csvMapping: (merchantId, mappingPkid) =>
    `/core/v1/merchant/${merchantId}/csv-mappings/${mappingPkid}`,
  // payment-service, by merchant pkid. The list answers `{ count, results }`, camelCase rows; the
  // export answers a csv/xlsx file. An unknown query key is a 400. Not Django's builder names.
  achReturns: merchantId => `/payment/v1/merchant/${merchantId}/ach-returns`,
  achReturnsExport: merchantId =>
    `/payment/v1/merchant/${merchantId}/ach-returns/export`,
  // Not `listPaymentReport` / `exportPaymentReport`: a regen would bring those Django builders back and
  // they would overwrite these. Merchant pkid; Django's query names and answer keys. Utils: paymentReport.ts.
  merchantPaymentReport: merchantId =>
    `/payment/v1/merchant/${merchantId}/payment-report`,
  merchantPaymentReportExport: merchantId =>
    `/payment/v1/merchant/${merchantId}/payment-report/export`,
  // Merchant pkid; Django's query names and answer keys. The Retry POST stays on Django's
  // `failedAutoPaymentsReport`, by merchant UUID.
  autopayFailures: merchantId =>
    `/payment/v1/merchant/${merchantId}/autopay-failures`,
  // Merchant and call pkids. List rows carry no transcript or recording; the detail adds `transcript`
  // and `recordingUrl`, a signed link that expires, so fetch it per open. Params: `utils/callData.ts`.
  callDataList: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/call-data`,
  callDataDetail: (merchantId, callPkid) =>
    `/ai-collector/v1/merchant/${merchantId}/call-data/${callPkid}`,
  callDataExport: merchantId =>
    `/ai-collector/v1/merchant/${merchantId}/call-data/export`,
  // Not `promiseReport` / `exportPromiseReport`: the generated Django builders of those names would
  // overwrite these. Merchant pkid. `{ count, results, counts }`, camelCase rows.
  merchantPromiseReport: merchantId =>
    `/core/v1/merchant/${merchantId}/promise-report`,
  merchantPromiseReportExport: merchantId =>
    `/core/v1/merchant/${merchantId}/promise-report/export`,
  // Merchant pkid; Django's query names and answer keys. The export answers the xlsx, named in
  // Content-Disposition.
  projectionReport: merchantId => `/core/v1/merchant/${merchantId}/projections`,
  projectionReportExport: merchantId =>
    `/core/v1/merchant/${merchantId}/projections/export`,
  // Reconciliation (`kind__exact=post_payment`) and Failed Action (`kind__exact=other`). Merchant and
  // row pkids. The list answers `{ count, capped, page, limit, results }`. Utils: failed-integration-actions.ts.
  failedIntegrationActions: merchantId =>
    `/core/v1/merchant/${merchantId}/failed-integration-actions`,
  failedIntegrationActionRetry: (merchantId, actionPkid) =>
    `/core/v1/merchant/${merchantId}/failed-integration-actions/${actionPkid}/retry`,
  failedIntegrationActionsRetryAll: merchantId =>
    `/core/v1/merchant/${merchantId}/failed-integration-actions/retry-all`,
  failedIntegrationActionClear: (merchantId, actionPkid) =>
    `/core/v1/merchant/${merchantId}/failed-integration-actions/${actionPkid}/clear`,
  failedIntegrationActionsClearAll: merchantId =>
    `/core/v1/merchant/${merchantId}/failed-integration-actions/clear-all`
}
